import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { Plus, Trash2, FileText, Shield, Sparkles, Calendar, AlertTriangle, Upload, X, Eye, Download, FolderOpen, RefreshCw, Thermometer, ClipboardCheck, SprayCan, Pencil, Save, Camera } from "lucide-react";
import { Modal } from "@/pages/Products";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const CATEGORIES = [
  { id: "hygiene",      label: "Hygiëne",             icon: ClipboardCheck,   color: "from-emerald-50 to-white", border: "border-emerald-200", accent: "text-emerald-700" },
  { id: "contracten",   label: "Contracten",          icon: FileText,   color: "from-amber-50 to-white",   border: "border-amber-200",   accent: "text-amber-700" },
  { id: "vergunningen", label: "Vergunningen",       icon: Shield,     color: "from-blue-50 to-white",    border: "border-blue-200",    accent: "text-blue-700" },
  { id: "overige",      label: "Overige Documenten", icon: FolderOpen, color: "from-zinc-50 to-white",    border: "border-zinc-200",    accent: "text-zinc-700" },
];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0,0,0,0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

const emptyForm = { name: "", category: "vergunningen", file_url: "", file_size: "", file_type: "", expires_at: "", restaurant_id: "" };

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = ["admin", "organization_admin", "platform_owner"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [restos, setRestos] = useState([]);
  const [searchParams] = useSearchParams();
  const initialOpen = searchParams.get("open") || "";
  const [tab, setTab] = useState("hygiene");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [restoFilter, setRestoFilter] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);

  const openPreview = (d) => {
    api.get(`/documents/${d.id}/access`).catch(() => {});
    setPreviewDoc(d);
  };

  const downloadDoc = (d) => {
    try {
      const a = document.createElement("a");
      a.href = d.file_url;
      a.download = d.name + (d.file_type?.startsWith("image/") ? "." + d.file_type.split("/")[1] : d.file_type === "application/pdf" ? ".pdf" : "");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      api.get(`/documents/${d.id}/access`).catch(() => {});
    } catch {
      toast.error("Download mislukt");
    }
  };

  const load = () => {
    const params = restoFilter ? `?restaurant_id=${restoFilter}` : "";
    return api.get(`/documents${params}`).then((r) => setDocs(r.data));
  };
  useEffect(() => { load(); }, [restoFilter]);
  useEffect(() => {
    if (isAdmin) api.get("/restaurants").then((r) => setRestos(r.data)).catch(() => {});
  }, [isAdmin]);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Bestand te groot (max 8 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setForm((f) => ({
      ...f,
      file_url: e.target.result,
      file_type: file.type,
      file_size: `${(file.size/1024).toFixed(0)} KB`,
    }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.file_url) { toast.error("Naam en bestand zijn verplicht"); return; }
    try {
      await api.post("/documents", { ...form, expires_at: form.expires_at || null, restaurant_id: form.restaurant_id || null });
      toast.success("Document toegevoegd");
      setShowForm(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const del = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId((c) => c === id ? null : c), 3500);
      return;
    }
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document verwijderd"); setConfirmDeleteId(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const filtered = useMemo(() => docs.filter((d) => d.category === tab), [docs, tab]);
  const counts = useMemo(() => {
    const c = { vergunningen: 0, hygiene: 0, contracten: 0, overige: 0 };
    for (const d of docs) c[d.category] = (c[d.category] || 0) + 1;
    return c;
  }, [docs]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="documents-page">
      <PageHeader
        overline="Compliance"
        title="Documenten"
        description="Beheer vergunningen, hygiëne-attesten en contracten. Krijg waarschuwingen wanneer iets verloopt."
        actions={isAdmin && (
          <button onClick={() => { setForm({ ...emptyForm, category: tab }); setShowForm(true); }}
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold" data-testid="documents-add-button">
            <Plus size={16} /> Nieuw document
          </button>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = tab === c.id;
          const badge = c.id === "hygiene" ? 7 : (counts[c.id] || 0);
          return (
            <button key={c.id} onClick={() => setTab(c.id)}
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-[#101828] text-white border-[#101828]" : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1B2D5C]"}`}
              data-testid={`docs-tab-${c.id}`}
            >
              <Icon size={14} /> {c.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${active ? "bg-white/20" : "bg-zinc-100 text-zinc-500"}`}>{badge}</span>
            </button>
          );
        })}
      </div>

      {isAdmin && restos.length > 1 && (
        <div className="mb-6 flex items-center gap-2">
          <label className="text-sm text-zinc-600">Filter op restaurant:</label>
          <select value={restoFilter} onChange={(e) => setRestoFilter(e.target.value)} className="h-9 px-3 border border-zinc-300 rounded-full text-sm bg-white" data-testid="docs-restaurant-filter">
            <option value="">Alle restaurants</option>
            {restos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      )}

      {tab === "hygiene" && <HygieneWeekSection isAdmin={isAdmin} restos={restos} restoFilter={restoFilter} initialOpen={initialOpen} />}

      {filtered.length === 0 ? (
        tab === "hygiene" ? null : (
        <div className="card-flat py-16 text-center" data-testid="documents-empty">
          <FileText className="mx-auto text-zinc-300 mb-3" size={32} />
          <p className="text-zinc-500 text-sm">Nog geen documenten in {CATEGORIES.find((c) => c.id === tab)?.label}.</p>
          {isAdmin && (
            <button onClick={() => { setForm({ ...emptyForm, category: tab }); setShowForm(true); }}
              className="mt-4 inline-flex items-center gap-2 h-9 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold">
              <Plus size={14} /> Voeg er één toe
            </button>
          )}
        </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="documents-grid">
          {filtered.map((d) => {
            const days = daysUntil(d.expires_at);
            const isExpired = days !== null && days < 0;
            const isSoon = days !== null && days >= 0 && days <= 30;
            return (
              <div key={d.id} className="card-flat p-5 flex flex-col" data-testid={`document-card-${d.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
                    <FileText size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] tracking-tight text-[#1B2D5C] truncate">{d.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{d.file_size || "—"} · {(d.file_type || "").split("/")[1] || "bestand"}</div>
                  </div>
                </div>
                {d.expires_at && (
                  <div className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded mb-3 self-start ${
                    isExpired ? "bg-red-50 text-red-700 border border-red-200" :
                    isSoon ? "bg-amber-50 text-amber-700 border border-amber-200" :
                    "bg-zinc-50 text-zinc-600 border border-zinc-200"
                  }`}>
                    {isExpired ? <AlertTriangle size={11} /> : <Calendar size={11} />}
                    {isExpired ? `Verlopen op ${d.expires_at}` : isSoon ? `Verloopt over ${days} dag${days===1?"":"en"}` : `Geldig tot ${d.expires_at}`}
                  </div>
                )}
                <div className="mt-auto flex items-center gap-2">
                  <button onClick={() => openPreview(d)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded text-xs font-semibold" data-testid={`open-document-${d.id}`}>
                    <Eye size={12} /> Bekijken
                  </button>
                  <button onClick={() => downloadDoc(d)}
                    className="inline-flex items-center justify-center gap-1.5 h-9 px-3 border border-zinc-300 hover:border-[#5BC044] text-zinc-700 hover:text-[#5BC044] rounded text-xs font-semibold" data-testid={`download-document-${d.id}`} title="Downloaden">
                    <Download size={12} />
                  </button>
                  {isAdmin && (
                    <button onClick={() => del(d.id)}
                      className={`h-9 px-3 rounded text-xs font-semibold ${confirmDeleteId === d.id ? "bg-red-600 hover:bg-red-700 text-white" : "border border-red-200 hover:bg-red-50 text-red-600"}`}
                      data-testid={`delete-document-${d.id}`}
                      title={confirmDeleteId === d.id ? "Klik nogmaals om te bevestigen" : "Verwijderen"}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <Modal title="Nieuw document" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-3" data-testid="document-form">
            <div>
              <label className="overline block mb-1.5">Bestand</label>
              <div className="flex items-center gap-3">
                {form.file_url && form.file_type?.startsWith("image/") ? (
                  <div className="w-16 h-16 rounded bg-cover bg-center border border-zinc-200 shrink-0" style={{ backgroundImage: `url(${form.file_url})` }} />
                ) : form.file_url ? (
                  <div className="w-16 h-16 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0"><FileText size={20} /></div>
                ) : (
                  <div className="w-16 h-16 rounded bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0"><Upload size={18} /></div>
                )}
                <label className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-3 border border-zinc-300 hover:border-[#5BC044] rounded-full text-sm cursor-pointer text-zinc-600">
                  <Upload size={14} /> Bestand kiezen (PDF/JPG/PNG)
                  <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="document-file-upload" />
                </label>
                {form.file_url && (
                  <button type="button" onClick={() => setForm({ ...form, file_url: "", file_size: "", file_type: "" })} className="text-zinc-400 hover:text-red-600 p-1">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="overline block mb-1.5">Naam</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" placeholder="Bijv. Drank & Horecavergunning" data-testid="document-name" />
            </div>
            <div>
              <label className="overline block mb-1.5">Categorie</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="form-input" data-testid="document-category">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            {restos.length > 0 && (
              <div>
                <label className="overline block mb-1.5">Restaurant (optioneel)</label>
                <select value={form.restaurant_id} onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })} className="form-input" data-testid="document-restaurant">
                  <option value="">— Voor de hele organisatie —</option>
                  {restos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="overline block mb-1.5">Vervaldatum (optioneel)</label>
              <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="form-input" data-testid="document-expires" />
            </div>
            <button type="submit" className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold" data-testid="document-submit">
              Toevoegen
            </button>
          </form>
        </Modal>
      )}

      {previewDoc && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)} data-testid="document-preview-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0"><FileText size={16} /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#1B2D5C] truncate" data-testid="preview-doc-name">{previewDoc.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{previewDoc.file_size || ""} · {(previewDoc.file_type || "bestand").split("/")[1] || previewDoc.file_type}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => downloadDoc(previewDoc)} className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded text-xs font-semibold" data-testid="preview-download">
                  <Download size={12} /> Downloaden
                </button>
                <button onClick={() => setPreviewDoc(null)} className="w-9 h-9 inline-flex items-center justify-center text-zinc-500 hover:text-[#1B2D5C] hover:bg-zinc-100 rounded" data-testid="preview-close" aria-label="Sluiten">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-zinc-50">
              {previewDoc.file_type?.startsWith("image/") ? (
                <div className="flex items-center justify-center p-6 min-h-full">
                  <img src={previewDoc.file_url} alt={previewDoc.name} className="max-w-full max-h-[78vh] object-contain rounded shadow-sm bg-white" />
                </div>
              ) : previewDoc.file_type === "application/pdf" || (previewDoc.file_url || "").toLowerCase().includes(".pdf") ? (
                <iframe src={previewDoc.file_url} title={previewDoc.name} className="w-full h-[78vh] border-0 bg-white" />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                  <FileText size={48} className="text-zinc-300 mb-3" />
                  <div className="font-semibold text-[#1B2D5C] mb-1">Voorvertoning niet beschikbaar</div>
                  <div className="text-sm text-zinc-500 mb-5">Dit bestandsformaat ({previewDoc.file_type || "onbekend"}) kan niet in de app worden weergegeven.</div>
                  <button onClick={() => downloadDoc(previewDoc)} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded text-sm font-semibold">
                    <Download size={14} /> Downloaden
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Hygiëne-weekkaarten: automatisch dagelijks opgemaakt om 22:00
// ────────────────────────────────────────────────────────────────────────
const HYGIENE_META = {
  bereiding:       { label: "Bereiding temperaturen", Icon: Thermometer,    tone: "from-orange-50 to-white",  border: "border-orange-200",  accent: "text-orange-700" },
  hygienecheck:    { label: "Hygiënecontrole",        Icon: ClipboardCheck, tone: "from-blue-50 to-white",    border: "border-blue-200",    accent: "text-blue-700" },
  ingangscontrole: { label: "Ingangscontrole",        Icon: ClipboardCheck, tone: "from-purple-50 to-white",  border: "border-purple-200",  accent: "text-purple-700" },
  leveranciers:    { label: "Leveranciers",           Icon: FileText,       tone: "from-teal-50 to-white",    border: "border-teal-200",    accent: "text-teal-700" },
  schoonmaak:      { label: "Schoonmaak rooster",      Icon: SprayCan,       tone: "from-emerald-50 to-white", border: "border-emerald-200", accent: "text-emerald-700" },
  temperatuur:     { label: "Temperatuur registratie", Icon: Thermometer,    tone: "from-amber-50 to-white",   border: "border-amber-200",   accent: "text-amber-700" },
  thermometers:    { label: "Thermometers",           Icon: Thermometer,    tone: "from-rose-50 to-white",    border: "border-rose-200",    accent: "text-rose-700" },
};
const HYGIENE_ORDER = ["bereiding", "hygienecheck", "ingangscontrole", "leveranciers", "schoonmaak", "temperatuur", "thermometers"];

function HygieneWeekSection({ isAdmin, restos, restoFilter, initialOpen }) {
  const { lang } = useLang();
  const [docs, setDocs] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [editing, setEditing] = useState(null);
  const [openedInitial, setOpenedInitial] = useState(false);

  const load = () => {
    const q = restoFilter ? `?restaurant_id=${restoFilter}` : "";
    return api.get(`/hygiene_documents${q}`)
      .then(r => setDocs(r.data || []))
      .catch(() => setDocs([]));
  };
  useEffect(() => { load(); }, [restoFilter]); // eslint-disable-line

  // Auto-open the editor when arriving from the dashboard "Compliance Status" widget
  useEffect(() => {
    if (openedInitial || !initialOpen || !docs || !docs.length) return;
    const match = docs.find((d) => d.type === initialOpen) || docs[0];
    if (match) {
      setEditing({
        restaurant_id: match.restaurant_id,
        restaurant_name: match.restaurant_name,
        chef_name: match.chef_name,
        type: initialOpen,
        week_nr: match.week_nr,
      });
      setOpenedInitial(true);
    }
  }, [initialOpen, docs, openedInitial]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const q = restoFilter ? `restaurant_id=${restoFilter}&lang=${lang}` : `lang=${lang}`;
      const r = await api.post(`/hygiene_documents/refresh?${q}`);
      toast.success(`${r.data.pdfs_generated} PDFs opnieuw opgemaakt voor week ${r.data.week_nr}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verversen mislukt");
    }
    setRefreshing(false);
  };

  // Convert pdf_b64 → Blob URL (memoised in modal lifecycle)
  const openPreview = (d) => {
    try {
      const bin = atob(d.pdf_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewDoc(d);
      setPreviewUrl(url);
    } catch {
      toast.error("PDF kon niet worden geopend");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const downloadPdf = (d) => {
    try {
      const bin = atob(d.pdf_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.bestandsnaam; a.rel = "noopener";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Download mislukt");
    }
  };

  // Group docs per restaurant
  const grouped = useMemo(() => {
    if (!docs) return [];
    const m = new Map();
    for (const d of docs) {
      if (!m.has(d.restaurant_id)) m.set(d.restaurant_id, { restaurant_id: d.restaurant_id, restaurant_name: d.restaurant_name, chef_name: d.chef_name, week_nr: d.week_nr, jaar: d.jaar, items: {} });
      m.get(d.restaurant_id).items[d.type] = d;
    }
    return Array.from(m.values()).sort((a, b) => a.restaurant_name.localeCompare(b.restaurant_name));
  }, [docs]);

  if (docs === null) {
    return (
      <div className="mb-8" data-testid="hygiene-week-loading">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card-flat p-5 animate-pulse">
              <div className="h-4 w-32 bg-zinc-100 rounded mb-3" />
              <div className="h-3 w-48 bg-zinc-100 rounded mb-2" />
              <div className="h-9 w-full bg-zinc-100 rounded mt-4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="card-flat p-8 mb-8 text-center" data-testid="hygiene-week-empty">
        <ClipboardCheck className="mx-auto text-zinc-300 mb-3" size={28} />
        <div className="font-semibold text-[#1B2D5C] mb-1">Nog geen weekdocumenten</div>
        <div className="text-sm text-zinc-500 mb-4">
          De dagelijkse update draait om 22:00. Klik hieronder om direct bij te werken.
        </div>
        {isAdmin && (
          <button onClick={onRefresh} disabled={refreshing}
            className="inline-flex items-center gap-2 h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold disabled:opacity-50"
            data-testid="hygiene-refresh-empty">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Nu bijwerken
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-8" data-testid="hygiene-week-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Wekelijkse hygiëne-rapporten</div>
          <h3 className="text-base font-bold text-[#1B2D5C]">
            Week {grouped[0]?.week_nr} · {grouped[0]?.jaar}
          </h3>
        </div>
        {isAdmin && (
          <button onClick={onRefresh} disabled={refreshing}
            className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-200 hover:border-[#5BC044] rounded-full text-sm font-semibold text-zinc-700 disabled:opacity-50"
            data-testid="hygiene-refresh-btn">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Bezig…" : "Nu bijwerken"}
          </button>
        )}
      </div>

      <div className="space-y-5">
        {grouped.map(g => (
          <div key={g.restaurant_id} data-testid={`hygiene-restaurant-${g.restaurant_id}`}>
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-sm font-semibold text-zinc-700">{g.restaurant_name}</div>
              <div className="text-xs text-zinc-500">Chef: <span className="font-semibold text-[#1B2D5C]">{g.chef_name}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
              {HYGIENE_ORDER.map(type => {
                const meta = HYGIENE_META[type];
                const d = g.items[type];
                const Icon = meta.Icon;
                const isPermanent = type === "thermometers" || type === "leveranciers";
                const isYtd = type !== "thermometers" && type !== "leveranciers"; // all non-permanent types are YTD now
                return (
                  <div key={type}
                    className={`card-flat p-4 bg-gradient-to-br ${meta.tone} ${meta.border} border`}
                    data-testid={`hygiene-card-${g.restaurant_id}-${type}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-9 h-9 rounded-md bg-white flex items-center justify-center ${meta.accent} shrink-0`}>
                        <Icon size={18} strokeWidth={1.7} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#1B2D5C] leading-tight min-h-[2.25rem]">{meta.label}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {isPermanent ? "Doorlopend" : `Week ${g.week_nr}`} · {d ? new Date(d.gegenereerd_op).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </div>
                      </div>
                    </div>
                    {d ? (
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => openPreview(d)}
                          className="flex-1 min-w-[70px] inline-flex items-center justify-center gap-1.5 h-9 px-2 rounded-full text-xs font-semibold border border-zinc-200 hover:border-[#1B2D5C] text-[#1B2D5C] bg-white"
                          data-testid={`hygiene-view-${g.restaurant_id}-${type}`}>
                          <Eye size={13} /> Bekijk
                        </button>
                        <button onClick={() => setEditing({ restaurant_id: g.restaurant_id, restaurant_name: g.restaurant_name, chef_name: g.chef_name, type, week_nr: g.week_nr })}
                          className="flex-1 min-w-[70px] inline-flex items-center justify-center gap-1.5 h-9 px-2 rounded-full text-xs font-semibold border border-zinc-200 hover:border-[#5BC044] text-[#5BC044] bg-white"
                          data-testid={`hygiene-edit-${g.restaurant_id}-${type}`}>
                          <Pencil size={13} /> Bewerken
                        </button>
                        <button onClick={() => downloadPdf(d)}
                          className="flex-1 min-w-[60px] inline-flex items-center justify-center gap-1.5 h-9 px-2 rounded-full text-xs font-semibold bg-[#5BC044] hover:bg-[#4FAA3A] text-white"
                          data-testid={`hygiene-download-${g.restaurant_id}-${type}`}>
                          <Download size={13} /> PDF
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500">Nog niet beschikbaar</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {previewDoc && previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closePreview} data-testid="hygiene-preview-modal">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
              <div>
                <div className="font-bold text-[#1B2D5C] text-sm">{HYGIENE_META[previewDoc.type]?.label} · {previewDoc.restaurant_name}</div>
                <div className="text-xs text-zinc-500">Week {previewDoc.week_nr} · Chef: {previewDoc.chef_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(previewDoc)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-xs font-semibold"
                  data-testid="hygiene-modal-download">
                  <Download size={13} /> Download
                </button>
                <button onClick={closePreview} className="w-9 h-9 rounded-md hover:bg-zinc-100 inline-flex items-center justify-center" data-testid="hygiene-modal-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe src={previewUrl} title="PDF preview" className="flex-1 w-full" />
          </div>
        </div>
      )}

      {editing && (
        <HygieneEditor
          data={editing}
          lang={lang}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// HygieneEditor — interactive HTML editor for schoonmaak/hygiene/temp
// State is loaded from GET /api/hygiene_state and saved via PUT (which also
// regenerates the PDF backend-side).
// ────────────────────────────────────────────────────────────────────────
const APPARATEN_ROWS = [
  ["Vriezers", "M", false], ["Verdampers", "M", false], ["Magazijnstellingen", "M", false],
  ["Schappen / stellingen", "M", false], ["Koelingen", "W", true], ["Frituren", "W", false],
  ["Afzuiging (incl. roosters)", "W", false], ["Wanden", "W", false], ["Tandoor", "D", false],
  ["Werkbanken", "D", true], ["Vloer", "D", true], ["Vaatwasser", "D", false],
  ["Afvalbakken", "D", true], ["Schoonmaakmateriaal", "D", true], ["Handcontactpunten", "D", true],
  ["Handenwasgelegenheid", "D", true], ["Spoelbakken", "D", true], ["Magnetron", "N", true],
  ["Snijdgereedschap", "N", true], ["Snijplanken", "N", true], ["Keukenmachines", "N", true],
  ["Kleine productiematerialen", "N", true],
];
const HYGIENE_REG_ROWS = [
  "Registratielijst ontvangst goederen", "Registratielijsten opslag (koeling/vriezer)",
  "Registratielijst schoonmaakrooster", "Registratielijst temperatuurregistratie",
  "Registratielijst leveranciers / HACCP",
];
const HYGIENE_UITV_ROWS = [
  "Detectie ongedierte / bestrijding", "Producten in FiFo opgeslagen",
  "Producten afgedekt opgeslagen", "Codering / houdbaarheidsdatum producten",
  "Gescheiden opslag rauw / bereid", "Juiste persoonlijke hygiëne (handschoenen / haarnet)",
  "Reiniging gereedschap en oppervlakken", "Allergenenbeheer (kruisbesmetting)",
];
const HYGIENE_ROOMS = ["Muren", "Deuren", "Vloeren", "Plafonds", "Verlichting", "Ventilatie / afzuiging", "Sanitair / toiletten", "Kleedruimte"];
const TEMP_APPARATEN = [
  ["Koelkast 1", "≤ 7°C"], ["Koelkast 2", "≤ 7°C"], ["Koelkast 3", "≤ 7°C"],
  ["Koelkast 4", "≤ 7°C"], ["Koelkast 5", "≤ 7°C"], ["Koelkast 6", "≤ 7°C"],
  ["Koelwerkbank 1", "≤ 7°C"], ["Koelwerkbank 2", "≤ 7°C"], ["Saladière", "≤ 7°C"],
  ["Vrieskist 1", "≤ -18°C"], ["Vrieskist 2", "≤ -18°C"], ["Vriezer", "≤ -18°C"],
  ["Vaatwasser was", "≥ 60°C"], ["Naspoeling", "≥ 80°C"],
];
const DAYS_NL_UI = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function HygieneEditor({ data, lang, onClose, onSaved }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = data.type;

  useEffect(() => {
    api.get(`/hygiene_state?restaurant_id=${data.restaurant_id}&type=${data.type}`)
      .then(r => setState(r.data?.overrides || {}))
      .catch(() => setState({}));
  }, [data.restaurant_id, data.type]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/hygiene_state", {
        restaurant_id: data.restaurant_id,
        type: data.type,
        lang,
        overrides: state,
      });
      toast.success("Opgeslagen — PDF bijgewerkt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
    setSaving(false);
  };

  const title = { schoonmaak: "Schoonmaak rooster", hygienecheck: "Hygiënecontrole", temperatuur: "Temperatuur registratie", bereiding: "Bereiding temperaturen", ingangscontrole: "Ingangscontrole", thermometers: "Thermometers", leveranciers: "Leveranciers" }[key];
  const isPermanent = key === "thermometers" || key === "leveranciers";
  const configurable = key === "schoonmaak" || key === "temperatuur";
  const autoFillable = key === "bereiding" || key === "ingangscontrole";
  const [showConfig, setShowConfig] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  const runAutoFill = async () => {
    if (!confirm(`Wil je '${title}' nu bijwerken op basis van je facturen en producten?`)) return;
    setAutoFilling(true);
    try {
      const r = await api.post("/hygiene_state/auto_fill", { restaurant_id: data.restaurant_id, type: key });
      setState(r.data.overrides || {});
      toast.success("Bijgewerkt — controleer en pas aan indien nodig");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bijwerken mislukt");
    }
    setAutoFilling(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="hygiene-editor-modal">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="font-bold text-[#1B2D5C] text-base">{title} · {data.restaurant_name}</div>
            <div className="text-xs text-zinc-500">
              {isPermanent ? "Doorlopende registratie (jaar-onafhankelijk)" : `Week ${data.week_nr}`} · Chef: {data.chef_name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {configurable && (
              <button onClick={() => setShowConfig(true)}
                className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 hover:border-[#1B2D5C] text-[#1B2D5C] rounded-full text-xs font-semibold"
                data-testid="hygiene-config-open">
                <FolderOpen size={13} /> Apparaten beheren
              </button>
            )}
            {autoFillable && (
              <button onClick={runAutoFill} disabled={autoFilling}
                className="inline-flex items-center gap-1.5 h-9 px-3 border border-[#5BC044]/40 hover:border-[#5BC044] text-[#5BC044] rounded-full text-xs font-semibold disabled:opacity-50"
                data-testid="hygiene-auto-fill">
                <RefreshCw size={13} className={autoFilling ? "animate-spin" : ""} /> {autoFilling ? "Bezig…" : "Nu bijwerken"}
              </button>
            )}
            <button onClick={save} disabled={state === null || saving}
              className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold disabled:opacity-50"
              data-testid="hygiene-editor-save">
              <Save size={14} /> {saving ? "Opslaan..." : "Opslaan"}
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-md hover:bg-zinc-100 inline-flex items-center justify-center"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {state === null ? (
            <div className="text-center py-8 text-zinc-500">Laden…</div>
          ) : key === "schoonmaak" ? (
            <SchoonmaakEditor state={state} setState={setState} restaurantId={data.restaurant_id} />
          ) : key === "hygienecheck" ? (
            <HygieneCheckEditor state={state} setState={setState} />
          ) : key === "temperatuur" ? (
            <TemperatuurEditor state={state} setState={setState} restaurantId={data.restaurant_id} />
          ) : key === "bereiding" ? (
            <BereidingEditor state={state} setState={setState} />
          ) : key === "ingangscontrole" ? (
            <IngangsControleEditor state={state} setState={setState} restaurantId={data.restaurant_id} />
          ) : key === "thermometers" ? (
            <ThermometersEditor state={state} setState={setState} />
          ) : (
            <LeveranciersEditor state={state} setState={setState} />
          )}
        </div>
      </div>
      {showConfig && (
        <ApparatenConfigModal
          restaurantId={data.restaurant_id}
          restaurantName={data.restaurant_name}
          kind={key === "schoonmaak" ? "schoonmaak" : "temperatuur"}
          onClose={() => setShowConfig(false)}
          onSaved={() => { setShowConfig(false); onSaved(); }}
        />
      )}
    </div>
  );
}

function SchoonmaakEditor({ state, setState, restaurantId }) {
  const cells = state.schoonmaak_cells || {};
  const [rows, setRows] = useState(APPARATEN_ROWS);
  useEffect(() => {
    if (!restaurantId) return;
    api.get(`/hygiene_config?restaurant_id=${restaurantId}`)
      .then(r => setRows(r.data?.schoonmaak_items || APPARATEN_ROWS))
      .catch(() => {});
  }, [restaurantId]);
  const setCell = (i, d, checked) => {
    const next = { ...cells, [`${i}-${d}`]: checked };
    setState({ ...state, schoonmaak_cells: next });
  };
  return (
    <div className="text-xs">
      <div className="text-xs text-zinc-500 mb-3">Vink handmatig aan/uit om de PDF af te stemmen op de werkelijke uitvoering. Alleen huidige week wordt aangepast.</div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#1A2B5E] text-white text-[10px]">
            <th className="px-2 py-1.5 text-left">Object</th>
            <th className="px-1 py-1.5">R</th>
            {DAYS_NL_UI.map(d => <th key={d} className="px-1 py-1.5">{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-zinc-100">
              <td className="px-2 py-1.5">{row[0]}</td>
              <td className="px-1 py-1.5 text-center font-mono text-zinc-500">{row[1]}</td>
              {[0,1,2,3,4,5,6].map(d => (
                <td key={d} className="px-1 py-1.5 text-center">
                  <input type="checkbox" checked={!!cells[`${i}-${d}`]}
                    onChange={e => setCell(i, d, e.target.checked)}
                    className="w-4 h-4 accent-[#5BC044] cursor-pointer"
                    data-testid={`schoonmaak-cb-${i}-${d}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HygieneCheckEditor({ state, setState }) {
  const cells = state.hygiene_cells || {};
  const setCell = (k, v) => setState({ ...state, hygiene_cells: { ...cells, [k]: v } });
  const Row = ({ label, keyName }) => (
    <tr className="border-b border-zinc-100">
      <td className="px-2 py-2">{label}</td>
      <td className="px-2 py-2 text-center">
        <input type="checkbox" checked={cells[keyName] !== false}
          onChange={e => setCell(keyName, e.target.checked)}
          className="w-4 h-4 accent-[#5BC044] cursor-pointer"
          data-testid={`hyg-cb-${keyName}`} />
      </td>
    </tr>
  );
  return (
    <div className="text-sm space-y-5">
      <div>
        <h4 className="text-sm font-bold text-[#1B2D5C] mb-2">Registraties</h4>
        <table className="w-full border-collapse text-xs">
          <thead><tr className="bg-[#1A2B5E] text-white"><th className="px-2 py-1.5 text-left">Onderwerp</th><th className="px-2 py-1.5 w-32">Aanwezig?</th></tr></thead>
          <tbody>{HYGIENE_REG_ROWS.map(r => <Row key={r} label={r} keyName={`reg-${r}`} />)}</tbody>
        </table>
      </div>
      <div>
        <h4 className="text-sm font-bold text-[#1B2D5C] mb-2">Uitvoering</h4>
        <table className="w-full border-collapse text-xs">
          <thead><tr className="bg-[#1A2B5E] text-white"><th className="px-2 py-1.5 text-left">Onderwerp</th><th className="px-2 py-1.5 w-32">Uitvoering ok?</th></tr></thead>
          <tbody>{HYGIENE_UITV_ROWS.map(r => <Row key={r} label={r} keyName={`uitv-${r}`} />)}</tbody>
        </table>
      </div>
      <div>
        <h4 className="text-sm font-bold text-[#1B2D5C] mb-2">Bedrijfsruimten</h4>
        <table className="w-full border-collapse text-xs">
          <thead><tr className="bg-[#1A2B5E] text-white"><th className="px-2 py-1.5 text-left">Ruimte</th><th className="px-2 py-1.5 w-32">Ok?</th></tr></thead>
          <tbody>{HYGIENE_ROOMS.map(r => <Row key={r} label={r} keyName={`room-${r}`} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

function TemperatuurEditor({ state, setState, restaurantId }) {
  const cells = state.temp_cells || {};
  const [rows, setRows] = useState(TEMP_APPARATEN);
  useEffect(() => {
    if (!restaurantId) return;
    api.get(`/hygiene_config?restaurant_id=${restaurantId}`)
      .then(r => {
        const items = r.data?.temp_apparaten;
        if (items && Array.isArray(items)) {
          // Only need [name, norm] for the UI (avg/sd/kind are backend-only)
          setRows(items.map(it => [it[0], it[1]]));
        }
      })
      .catch(() => {});
  }, [restaurantId]);
  const setCell = (ai, di, val) => {
    const next = { ...cells };
    if (val === "" || val === null) delete next[`${ai}-${di}`];
    else next[`${ai}-${di}`] = parseFloat(val);
    setState({ ...state, temp_cells: next });
  };
  return (
    <div className="text-xs">
      <div className="text-xs text-zinc-500 mb-3">Vul de daadwerkelijke temperatuurmetingen in (°C). Alleen huidige week wordt aangepast.</div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#1A2B5E] text-white text-[10px]">
            <th className="px-2 py-1.5 text-left">Apparaat</th>
            <th className="px-2 py-1.5">Norm</th>
            {DAYS_NL_UI.map(d => <th key={d} className="px-1 py-1.5">{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ai) => (
            <tr key={ai} className="border-b border-zinc-100">
              <td className="px-2 py-1.5 font-medium">{row[0]}</td>
              <td className="px-2 py-1.5 text-center text-zinc-500 font-mono">{row[1]}</td>
              {[0,1,2,3,4,5,6].map(d => (
                <td key={d} className="px-1 py-1">
                  <input type="number" step="0.1"
                    value={cells[`${ai}-${d}`] ?? ""}
                    onChange={e => setCell(ai, d, e.target.value)}
                    placeholder="—"
                    className="w-14 h-7 border border-zinc-200 rounded px-1 text-center font-mono text-xs focus:outline-none focus:border-[#5BC044]"
                    data-testid={`temp-input-${ai}-${d}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// BereidingEditor — user-filled preparation & serving temp log
// ────────────────────────────────────────────────────────────────────────
const BEREIDING_SECTIONS = [
  { key: "cool",       title: "Afkoelen",                                      needsProduct: true,  standards: [{ label: "Start: uur", std: "" }, { label: "Na 2u", std: "≤ 20°C" }, { label: "Na 5u", std: "≤ 7°C" }], freq: "1 x per week" },
  { key: "cooked",     title: "Kerntemperatuur — Bereid product",              needsProduct: true,  standards: [{ std: "≥ 75°C" }, { std: "≥ 75°C" }], freq: "1 x per week" },
  { key: "reheat",     title: "Kerntemperatuur — Opgewarmd product",            needsProduct: true,  standards: [{ std: "≥ 60°C" }, { std: "≥ 60°C" }], freq: "1 x per week" },
  { key: "fryer",      title: "Frituur",                                        needsProduct: false, standards: [{ label: "Frituur 1", std: "≤ 175°C" }, { label: "Frituur 2", std: "≤ 175°C" }], freq: "1 x per frituur/wk" },
];

function BereidingEditor({ state, setState }) {
  const rows = state.bereiding_rows || {};
  const updateRow = (sectionKey, idx, field, val) => {
    const sect = [...(rows[sectionKey] || [])];
    while (sect.length <= idx) sect.push({});
    sect[idx] = { ...sect[idx], [field]: val };
    setState({ ...state, bereiding_rows: { ...rows, [sectionKey]: sect } });
  };
  return (
    <div className="space-y-6 text-sm">
      <div className="text-xs text-zinc-500">
        Vul per sectie de daadwerkelijke gemeten temperaturen in. Alleen huidige week wordt aangepast.
      </div>
      {BEREIDING_SECTIONS.map(section => {
        const totalRows = section.standards.length + 2; // fixed standards + 2 free rows
        return (
          <div key={section.key}>
            <h4 className="text-sm font-bold text-[#1B2D5C] mb-1">{section.title}</h4>
            <div className="text-xs text-zinc-500 mb-2">Invulfrequentie: {section.freq}</div>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#1A2B5E] text-white">
                  <th className="px-2 py-1.5 text-left w-24">Datum</th>
                  <th className="px-2 py-1.5 text-left w-20">Tijd</th>
                  <th className="px-2 py-1.5 text-left">{section.needsProduct ? "Product" : "Meting"}</th>
                  <th className="px-2 py-1.5 w-24">Temp (°C)</th>
                  <th className="px-2 py-1.5 w-28">Standaard</th>
                  <th className="px-2 py-1.5 text-left">Corrigerende actie</th>
                  <th className="px-2 py-1.5 w-20">Init.</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: totalRows }).map((_, i) => {
                  const row = (rows[section.key] || [])[i] || {};
                  const std = section.standards[i];
                  return (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-1 py-1"><input type="date" value={row.date || ""} onChange={e => updateRow(section.key, i, "date", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs" data-testid={`ber-${section.key}-date-${i}`} /></td>
                      <td className="px-1 py-1"><input type="time" value={row.time || ""} onChange={e => updateRow(section.key, i, "time", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs" data-testid={`ber-${section.key}-time-${i}`} /></td>
                      <td className="px-1 py-1">
                        <input value={row.product || (std && !section.needsProduct && std.label) || ""}
                               onChange={e => updateRow(section.key, i, "product", e.target.value)}
                               className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder={section.needsProduct ? "Product" : "Meting"} />
                      </td>
                      <td className="px-1 py-1"><input type="number" step="0.1" value={row.temp || ""} onChange={e => updateRow(section.key, i, "temp", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" placeholder="—" /></td>
                      <td className="px-1 py-1 text-center text-zinc-500 text-[10px] font-mono">{std ? std.std : ""}</td>
                      <td className="px-1 py-1"><input value={row.action || ""} onChange={e => updateRow(section.key, i, "action", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="—" /></td>
                      <td className="px-1 py-1"><input value={row.initials || ""} onChange={e => updateRow(section.key, i, "initials", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" maxLength={4} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// IngangsControleEditor — supplier delivery inspection log
// ────────────────────────────────────────────────────────────────────────
function IngangsControleEditor({ state, setState, restaurantId }) {
  const rows = state.ingang_rows || [];
  const remark = state.ingang_opmerkingen || "";
  const [scanning, setScanning] = useState(false);
  const updateRow = (i, field, val) => {
    const next = [...rows];
    while (next.length <= i) next.push({});
    next[i] = { ...next[i], [field]: val };
    setState({ ...state, ingang_rows: next });
  };
  const removeRow = (i) => setState({ ...state, ingang_rows: rows.filter((_, j) => j !== i) });

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("Bestand kon niet worden gelezen"));
    reader.readAsDataURL(file);
  });

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Alleen afbeeldingen of PDF-bestanden worden ondersteund");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Bestand is te groot (max 15 MB)");
      return;
    }
    if (!restaurantId) {
      toast.error("Restaurant niet gevonden");
      return;
    }
    setScanning(true);
    try {
      const b64 = await readAsBase64(file);
      const r = await api.post("/hygiene_state/ingang_from_invoice", {
        restaurant_id: restaurantId,
        image_b64: b64,
        mime_type: file.type || (isPdf ? "application/pdf" : "image/jpeg"),
      });
      const returned = Array.isArray(r.data?.rows) ? r.data.rows : [];
      if (returned.length === 0) {
        toast.info("Geen regels herkend — vul handmatig aan");
        return;
      }
      const existing = (state.ingang_rows || []).filter(
        (row) => row && (row.date || row.supplier || row.product),
      );
      setState({
        ...state,
        ingang_rows: [...existing, ...returned],
      });
      toast.success(`${returned.length} regel(s) ingelezen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inlezen mislukt, probeer opnieuw");
    } finally {
      setScanning(false);
    }
  };

  // Always show at least 8 rows for user convenience, up to 20
  const rowCount = Math.min(20, Math.max(8, rows.length + 1));

  return (
    <div className="space-y-4 text-xs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          Registreer per levering: temperatuur en visuele controle.
          <br />
          <b>Legenda:</b> V = Vers · C = Conserven/Droge waren · D = Diepvries · G = Goed · F = Fout
        </div>
        <div className="flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-1.5 h-9 px-3 border rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${
            scanning
              ? "border-zinc-200 text-zinc-400 cursor-wait"
              : "border-[#1B2D5C]/40 hover:border-[#1B2D5C] text-[#1B2D5C]"
          }`}
          data-testid="ingang-upload-invoice"
        >
          {scanning ? (
            <RefreshCw size={13} className="animate-spin" />
          ) : (
            <Upload size={13} />
          )}
          {scanning ? "Inlezen…" : "Bon uploaden"}
          <input
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            disabled={scanning}
            onChange={onFileSelected}
            data-testid="ingang-upload-input"
          />
        </label>
        <label
          className={`inline-flex items-center gap-1.5 h-9 px-3 border rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${
            scanning
              ? "border-zinc-200 text-zinc-400 cursor-wait"
              : "border-[#5BC044]/40 hover:border-[#5BC044] text-[#5BC044]"
          }`}
          data-testid="ingang-take-photo"
        >
          <Camera size={13} />
          Foto maken
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            disabled={scanning}
            onChange={onFileSelected}
            data-testid="ingang-camera-input"
          />
        </label>
        </div>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#1A2B5E] text-white">
            <th className="px-2 py-1.5 text-left w-28">Datum</th>
            <th className="px-2 py-1.5 text-left">Leverancier</th>
            <th className="px-2 py-1.5 text-left">Product</th>
            <th className="px-2 py-1.5 w-14">Soort</th>
            <th className="px-2 py-1.5 w-20">Temp (°C)</th>
            <th className="px-2 py-1.5 text-left">Verpakking</th>
            <th className="px-2 py-1.5 w-14">G/F</th>
            <th className="px-2 py-1.5 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => {
            const r = rows[i] || {};
            return (
              <tr key={i} className="border-b border-zinc-100">
                <td className="px-1 py-1"><input type="date" value={r.date || ""} onChange={e => updateRow(i, "date", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs" data-testid={`ingang-date-${i}`} /></td>
                <td className="px-1 py-1"><input value={r.supplier || ""} onChange={e => updateRow(i, "supplier", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Bijv. Sligro" /></td>
                <td className="px-1 py-1"><input value={r.product || ""} onChange={e => updateRow(i, "product", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Kipfilet, tomaat…" /></td>
                <td className="px-1 py-1">
                  <select value={r.kind || ""} onChange={e => updateRow(i, "kind", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs bg-white">
                    <option value=""></option><option value="V">V</option><option value="C">C</option><option value="D">D</option>
                  </select>
                </td>
                <td className="px-1 py-1"><input type="number" step="0.1" value={r.temp || ""} onChange={e => updateRow(i, "temp", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" placeholder="—" /></td>
                <td className="px-1 py-1"><input value={r.packaging || ""} onChange={e => updateRow(i, "packaging", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="OK / kapot / vies" /></td>
                <td className="px-1 py-1">
                  <select value={r.result || ""} onChange={e => updateRow(i, "result", e.target.value)}
                          className={`w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-bold bg-white ${r.result === "G" ? "text-emerald-600" : r.result === "F" ? "text-red-600" : ""}`}>
                    <option value=""></option><option value="G">G</option><option value="F">F</option>
                  </select>
                </td>
                <td className="px-1 py-1 text-center">
                  {(r.date || r.supplier || r.product) && (
                    <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" data-testid={`ingang-remove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div>
        <label className="block text-xs font-semibold text-[#1B2D5C] mb-1">Opmerkingen</label>
        <textarea value={remark} onChange={e => setState({ ...state, ingang_opmerkingen: e.target.value })}
          className="w-full h-20 border border-zinc-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-[#5BC044]"
          placeholder="Bijv. incidenten, retourzendingen, aanvullende observaties…" />
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// ThermometersEditor — periodic thermometer calibration log
// Overrides key: thermometer_rows = [{ date, thermometer, boiling, ice, deviation, action, initials }]
// ────────────────────────────────────────────────────────────────────────
function ThermometersEditor({ state, setState }) {
  const rows = state.thermometer_rows || [];
  const updateRow = (i, field, val) => {
    const next = [...rows];
    while (next.length <= i) next.push({});
    next[i] = { ...next[i], [field]: val };
    // Auto-compute deviation when boiling or ice changes (100°C / 0°C reference)
    if (field === "boiling" || field === "ice") {
      const b = parseFloat(field === "boiling" ? val : next[i].boiling);
      const ic = parseFloat(field === "ice" ? val : next[i].ice);
      if (!Number.isNaN(b) || !Number.isNaN(ic)) {
        const dev = Math.max(
          Number.isNaN(b) ? 0 : Math.abs(b - 100),
          Number.isNaN(ic) ? 0 : Math.abs(ic - 0),
        );
        next[i].deviation = dev.toFixed(1);
      }
    }
    setState({ ...state, thermometer_rows: next });
  };
  const removeRow = (i) => setState({ ...state, thermometer_rows: rows.filter((_, j) => j !== i) });
  const rowCount = Math.min(15, Math.max(8, rows.length + 1));

  return (
    <div className="space-y-4 text-xs">
      <div className="text-xs text-zinc-500">
        Registreer per thermometer de kalibratietest. <b>Referentie:</b> kokend water ≈ 100°C · smeltend ijs ≈ 0°C.
        Afwijking &gt; 1.0°C → kalibreren of vervangen. Invulfrequentie: minimaal 1 x per 3 maanden per thermometer.
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#1A2B5E] text-white">
            <th className="px-2 py-1.5 text-left w-28">Datum</th>
            <th className="px-2 py-1.5 text-left">Thermometer</th>
            <th className="px-2 py-1.5 w-24">Kokend water (°C)</th>
            <th className="px-2 py-1.5 w-24">Smeltend ijs (°C)</th>
            <th className="px-2 py-1.5 w-20">Afwijking (°C)</th>
            <th className="px-2 py-1.5 text-left">Corrigerende maatregel</th>
            <th className="px-2 py-1.5 w-16">Paraaf</th>
            <th className="px-2 py-1.5 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => {
            const r = rows[i] || {};
            const dev = parseFloat(r.deviation);
            const devClass = !Number.isNaN(dev) ? (dev > 1.0 ? "text-red-600 font-bold" : "text-emerald-600 font-semibold") : "";
            return (
              <tr key={i} className="border-b border-zinc-100">
                <td className="px-1 py-1"><input type="date" value={r.date || ""} onChange={e => updateRow(i, "date", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs" data-testid={`therm-date-${i}`} /></td>
                <td className="px-1 py-1"><input value={r.thermometer || ""} onChange={e => updateRow(i, "thermometer", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Bijv. Digitaal #1" data-testid={`therm-name-${i}`} /></td>
                <td className="px-1 py-1"><input type="number" step="0.1" value={r.boiling || ""} onChange={e => updateRow(i, "boiling", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" placeholder="100.0" data-testid={`therm-boiling-${i}`} /></td>
                <td className="px-1 py-1"><input type="number" step="0.1" value={r.ice || ""} onChange={e => updateRow(i, "ice", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" placeholder="0.0" data-testid={`therm-ice-${i}`} /></td>
                <td className={`px-1 py-1 text-center text-xs font-mono ${devClass}`} data-testid={`therm-deviation-${i}`}>{r.deviation || ""}</td>
                <td className="px-1 py-1"><input value={r.action || ""} onChange={e => updateRow(i, "action", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="—" /></td>
                <td className="px-1 py-1"><input value={r.initials || ""} onChange={e => updateRow(i, "initials", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-1 text-xs text-center font-mono" maxLength={4} /></td>
                <td className="px-1 py-1 text-center">
                  {(r.date || r.thermometer || r.boiling || r.ice) && (
                    <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" data-testid={`therm-remove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// LeveranciersEditor — approved supplier registry
// Overrides key: leveranciers_rows = [{ name, supplies, notes }]
// ────────────────────────────────────────────────────────────────────────
function LeveranciersEditor({ state, setState }) {
  const rows = state.leveranciers_rows || [];
  const updateRow = (i, field, val) => {
    const next = [...rows];
    while (next.length <= i) next.push({});
    next[i] = { ...next[i], [field]: val };
    setState({ ...state, leveranciers_rows: next });
  };
  const removeRow = (i) => setState({ ...state, leveranciers_rows: rows.filter((_, j) => j !== i) });
  const rowCount = Math.min(25, Math.max(10, rows.length + 1));

  return (
    <div className="space-y-4 text-xs">
      <div className="text-xs text-zinc-500">
        Registreer alle goedgekeurde leveranciers waarvan het restaurant producten inkoopt. Vermeld eventuele bijzonderheden (certificaten, allergenen, contactgegevens).
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#1A2B5E] text-white">
            <th className="px-2 py-1.5 text-left">Naam leverancier</th>
            <th className="px-2 py-1.5 text-left">Leverancier van</th>
            <th className="px-2 py-1.5 text-left">Bijzonderheden</th>
            <th className="px-2 py-1.5 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, i) => {
            const r = rows[i] || {};
            return (
              <tr key={i} className="border-b border-zinc-100">
                <td className="px-1 py-1"><input value={r.name || ""} onChange={e => updateRow(i, "name", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Bijv. Sligro" data-testid={`lev-name-${i}`} /></td>
                <td className="px-1 py-1"><input value={r.supplies || ""} onChange={e => updateRow(i, "supplies", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Bijv. Vlees, groente, dranken" data-testid={`lev-supplies-${i}`} /></td>
                <td className="px-1 py-1"><input value={r.notes || ""} onChange={e => updateRow(i, "notes", e.target.value)} className="w-full h-7 border border-zinc-200 rounded px-2 text-xs" placeholder="Contactpersoon, certificaat, allergenen…" data-testid={`lev-notes-${i}`} /></td>
                <td className="px-1 py-1 text-center">
                  {(r.name || r.supplies) && (
                    <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-700" data-testid={`lev-remove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// ApparatenConfigModal — per-restaurant appliance/item list manager
// Applies retroactively from Jan 1 → today (backend regenerates YTD PDFs).
// ────────────────────────────────────────────────────────────────────────
const FREQ_OPTS = [
  { v: "D", l: "Dagelijks" }, { v: "W", l: "Wekelijks" }, { v: "M", l: "Maandelijks" }, { v: "N", l: "Naar gebruik" },
];
const TEMP_KIND_OPTS = [
  { v: "koel", l: "Koeling (≤ 7°C)", avg: 4.0, sd: 0.6, norm: "≤ 7°C" },
  { v: "vries", l: "Vriezer (≤ -18°C)", avg: -21.0, sd: 0.7, norm: "≤ -18°C" },
  { v: "was", l: "Vaatwasser was (≥ 60°C)", avg: 65.0, sd: 1.0, norm: "≥ 60°C" },
  { v: "naspoel", l: "Naspoeling (≥ 80°C)", avg: 84.0, sd: 1.0, norm: "≥ 80°C" },
];

function ApparatenConfigModal({ restaurantId, restaurantName, kind, onClose, onSaved }) {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const isSchoonmaak = kind === "schoonmaak";

  useEffect(() => {
    api.get(`/hygiene_config?restaurant_id=${restaurantId}`)
      .then(r => setItems(isSchoonmaak ? r.data.schoonmaak_items : r.data.temp_apparaten))
      .catch(() => setItems([]));
  }, [restaurantId, isSchoonmaak]);

  const update = (i, col, val) => {
    const next = items.map(x => [...x]);
    next[i][col] = val;
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  const add = () => {
    if (isSchoonmaak) setItems([...items, ["Nieuw item", "W", false]]);
    else setItems([...items, ["Nieuwe koeling", "≤ 7°C", 4.0, 0.6, "koel"]]);
  };
  const changeTempKind = (i, kindVal) => {
    const preset = TEMP_KIND_OPTS.find(k => k.v === kindVal);
    if (!preset) return;
    const next = items.map(x => [...x]);
    next[i][1] = preset.norm;
    next[i][2] = preset.avg;
    next[i][3] = preset.sd;
    next[i][4] = preset.v;
    setItems(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { restaurant_id: restaurantId };
      if (isSchoonmaak) body.schoonmaak_items = items;
      else body.temp_apparaten = items;
      const r = await api.put("/hygiene_config", body);
      toast.success(`Opgeslagen — ${r.data.pdfs_regenerated} PDFs opnieuw opgemaakt`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="apparaten-config-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="font-bold text-[#1B2D5C] text-base">
              {isSchoonmaak ? "Schoonmaakitems beheren" : "Temperatuurapparaten beheren"} · {restaurantName}
            </div>
            <div className="text-xs text-zinc-500">Wijzigingen gelden voor het hele jaar (alle bestaande weken worden opnieuw opgemaakt).</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={items === null || saving}
              className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold disabled:opacity-50"
              data-testid="apparaten-save">
              <Save size={14} /> {saving ? "Opslaan..." : "Opslaan & Toepassen"}
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-md hover:bg-zinc-100 inline-flex items-center justify-center"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {items === null ? (
            <div className="text-center py-8 text-zinc-500">Laden…</div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs text-zinc-500">{items.length} items</div>
                <button onClick={add}
                  className="inline-flex items-center gap-1.5 h-8 px-3 bg-white border border-zinc-300 hover:border-[#5BC044] text-[#5BC044] rounded text-xs font-semibold"
                  data-testid="apparaten-add">
                  <Plus size={13} /> Item toevoegen
                </button>
              </div>
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-[#1A2B5E] text-white">
                    <th className="px-2 py-1.5 text-left">Naam</th>
                    {isSchoonmaak ? (
                      <>
                        <th className="px-2 py-1.5 w-32">Frequentie</th>
                        <th className="px-2 py-1.5 w-24">R+D (allergen)</th>
                      </>
                    ) : (
                      <>
                        <th className="px-2 py-1.5 w-40">Type</th>
                        <th className="px-2 py-1.5 w-24">Norm</th>
                      </>
                    )}
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-1 py-1">
                        <input value={row[0]} onChange={e => update(i, 0, e.target.value)}
                          className="w-full h-8 border border-zinc-200 rounded px-2 text-xs" data-testid={`app-name-${i}`} />
                      </td>
                      {isSchoonmaak ? (
                        <>
                          <td className="px-1 py-1">
                            <select value={row[1]} onChange={e => update(i, 1, e.target.value)}
                              className="w-full h-8 border border-zinc-200 rounded px-1 text-xs bg-white" data-testid={`app-freq-${i}`}>
                              {FREQ_OPTS.map(o => <option key={o.v} value={o.v}>{o.v} — {o.l}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1 text-center">
                            <input type="checkbox" checked={!!row[2]} onChange={e => update(i, 2, e.target.checked)}
                              className="w-4 h-4 accent-[#5BC044]" data-testid={`app-allergen-${i}`} />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-1 py-1">
                            <select value={row[4]} onChange={e => changeTempKind(i, e.target.value)}
                              className="w-full h-8 border border-zinc-200 rounded px-1 text-xs bg-white" data-testid={`app-kind-${i}`}>
                              {TEMP_KIND_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1 text-center text-zinc-500 font-mono">{row[1]}</td>
                        </>
                      )}
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700" data-testid={`app-remove-${i}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

